import { SAVE_KEYS } from "../Constants/SaveKeys";
import { ENGINE_DEBUG_MODE, GAME_DEBUG_MODE } from "../../engine/Constants/Constants";
import { IMailInstanceData } from "../Types/IMailInstanceData";
import { MailState } from "../Types/MailState";
import { MailType } from "../Types/MailType";
import { MailCore } from "./MailCore";
import { IMedCaseJSON } from "../Types/IMedCaseJSON";
import { AchievementID } from "../Data/Achievements";
import { IUserData } from "./HospitalNetCore";
import { AnalyticsEventID } from "../Constants/AnalyticsEventIDs";
import { GameModes } from "../Types/GameModes";
import { ICharacterGeneratorSettings } from "./CharacterPortraitGenerator";
import { TContactID } from "../Types/TContactID";
import { INarrativeState } from "../Types/INarrativeState";
import { DAY_START_TIMESTAMP, GAME_START_TIMESTAMP, ONE_DAY_MILLISECONDS } from "../Constants/Constants";
import { ProcessState } from "../Constants/ProcessState";
import { TConversationId, TFlagId } from "../Types/SharedTypes";

let _UPDATE_GAME_TIME_TIMER: number | null = null;

export interface ISlotData {
    displayName: string;
    timestamp: number;
    gameVersion: string;
    activeCases: IMedCaseJSON[];
    mailbox: IMailInstanceData[]
    seenFTUTutorial: boolean;
    pastCases: Record<string, IMedCaseJSON>;
    numOfSolvedCases: number;
    numOfIncorrectDiagnoses: number;
    docScore: number;
    contacts: Array<TContactID>;
    achievements: AchievementID[];
    gameMode: GameModes;
    characterCreatorSettings: ICharacterGeneratorSettings;
    hometown: string;
    birthdate: {
        day: number;
        month: number;
    };
    day: number;
    narrativeState: INarrativeState;
    currentDayTimer: number;
}

export const PER_SLOT_KEYS = [
    SAVE_KEYS.DisplayName,
    SAVE_KEYS.LastUpdatedTimestamp,
    SAVE_KEYS.Version,
    SAVE_KEYS.ActiveCases,
    SAVE_KEYS.MailboxContents,
    SAVE_KEYS.SeenFTUTutorial,
    SAVE_KEYS.PastCases,
    SAVE_KEYS.NumOfSolvedCases,
    SAVE_KEYS.NumOfIncorrectDiagnoses,
    SAVE_KEYS.DocScore,
    SAVE_KEYS.Achievements,
    SAVE_KEYS.GameMode,
    SAVE_KEYS.CharacterCreatorSettings,
    SAVE_KEYS.Hometown,
    SAVE_KEYS.Birthdate,
    SAVE_KEYS.Day,
    SAVE_KEYS.NarrativeState,
    SAVE_KEYS.CurrentDayTimer,
];

class PlayerDataSingletonClass {

    // @ts-ignore
    private _activeSaveSlot: 0 | 1 | 2 = -1;
    private _saveSlotData: Record<0 | 1 | 2, ISlotData | null> = {
        0: null,
        1: null,
        2: null,
    };

    // shared saved data
    private _knownUrls: string[] = [
        "http://www.drugle.com",
        "about:credits",
    ];
    private _mailboxZoom: number = 1;
    private _shouldAutoFullscreen: boolean = false;
    private _muteMusic: boolean = false;
    private _crtEffectEnabled: boolean = false;
    private _authKey: string = "";
    private _authPlatform: string = "none";
    private _muteSFX: boolean = false;
    private _use24hrClock: boolean = true;

    // per-slot data
    // private _gameVersion: string = "0.0.0";
    // private _displayName: string = "";
    // private _hometown: string = "";
    // private _birthdate: {
    //     day: number;
    //     month: number;
    // } = {
    //     day: -1,
    //     month: -1,
    // };
    // private _activeCases: IMedCaseJSON[] = [];
    // private _mailbox: IMailInstanceData[] = [
    //     {
    //         id: "INTRODUCTION",
    //         dateReceived: Date.now(),
    //         state: MailState.UNREAD,
    //         type: MailType.INFO,
    //     },
    // ];
    // private _seenFTUTutorial: boolean = false;
    // private _pastCases: Record<string, IMedCaseJSON> = {};
    // private _numOfSolvedCases: number = 0;
    // private _numOfIncorrectDiagnoses: number = 0;
    // private _docScore: number = 0;
    // private _achievements: AchievementID[] = [];
    // private _gameMode: GameModes = GameModes.CASUAL;
    // private _characterCreatorSettings: ICharacterGeneratorSettings = {};

    // Properties
    private _initialized: boolean = false;

    private _dirty: Array<SAVE_KEYS> = [];

    constructor() {}

    public hasSaveSlot(_slot: 0 | 1 | 2): boolean {
        return Boolean(this._saveSlotData[_slot]);
    }

    public setActiveSaveSlot(_slot: 0 | 1 | 2): void {
        this._activeSaveSlot = _slot;
    }

    public setSlotDataToSlot(
        _slotData: ISlotData,
        _slotInd: -1 | 0 | 1 | 2 = this._activeSaveSlot
    ): void {
        if(_slotInd === -1) throw new Error("No valid active slot set");
        this._saveSlotData[_slotInd] = JSON.parse(JSON.stringify(_slotData));
        switch(_slotInd) {
            case 0:
                this.dirtify(SAVE_KEYS.SaveSlot0);
                break;
            case 1:
                this.dirtify(SAVE_KEYS.SaveSlot1);
                break;
            case 2:
                this.dirtify(SAVE_KEYS.SaveSlot2);
                break;
        }
        this.dirtify(PER_SLOT_KEYS);
    }

    public getSaveSlotDataRef(_ind: -1 | 0 | 1 | 2 = this._activeSaveSlot): ISlotData | null {
        if(_ind === -1) {
            throw new Error("Invalid save slot requested!");
        }
        return this._saveSlotData[_ind];
    }

    public initializeEmptySlot(): ISlotData {
        return {
            displayName: "",
            day: 1,
            currentDayTimer: 0,
            narrativeState: {
                seenConversations: [],
                flags: [],
            },
            contacts: ["TestContact1"],
            gameMode: GameModes.CASUAL,
            gameVersion: __VERSION,
            mailbox: [
                {
                    id: "INTRODUCTION",
                    dateReceived: GAME_START_TIMESTAMP + DAY_START_TIMESTAMP,
                    state: MailState.UNREAD,
                    type: MailType.INFO,
                },
            ],
            seenFTUTutorial: false,
            numOfIncorrectDiagnoses: 0,
            numOfSolvedCases: 0,
            docScore: 0,
            achievements: [],
            activeCases: [],
            pastCases: {},
            hometown: "",
            birthdate: {
                day: -1,
                month: -1,
            },
            characterCreatorSettings: null,
            timestamp: 0
        };
    }

    public isInitialized(): boolean {
        return this._initialized;
    }

    public dirtify(key: (string | SAVE_KEYS) | (string[] | SAVE_KEYS[])): void {
        if(Array.isArray(key)) {
            this._dirty.push(...(key as SAVE_KEYS[]));
        } else {
            this._dirty.push(key as SAVE_KEYS);
        }
    }

    public isDirty(): boolean {
        return this._dirty.length > 0;
    }

    public incrementDay(): void {
        this.setDay(this.getDay() + 1);
        this.setDayTimer(0);
    }

    public setConversationSeen(_id: TConversationId): void {
        const seenConvos = this._saveSlotData[this._activeSaveSlot]
            .narrativeState.seenConversations;
        if(seenConvos.indexOf(_id) !== -1) {
            console.warn("Conversation %s is already in saved data", _id);
            return;
        }
        seenConvos.push(_id);
        this.dirtify(SAVE_KEYS.NarrativeState);
    }

    public hasSeenConversation(_id: TConversationId): boolean {
        return (this._saveSlotData[this._activeSaveSlot]
            .narrativeState.seenConversations.indexOf(_id) !== -1);
    }

    public getGameTime(): number {
        // let now = new Date();
        // let millisecondsSinceMidnight =
        //     (now.getHours() * 3600000) +
        //     (now.getMinutes() * 60000) +
        //     (now.getSeconds() * 1000) + now.getMilliseconds();

        return Date.now();
    }

    public hasAnyActiveExamsTreatments(): boolean {
        const cases = this.getCases();
        return Boolean(
            cases.find((e) => {
                return (
                    Boolean(e.attemptedExaminations.find(
                        (x) => x.state === ProcessState.EXAM_IN_PROGRESS
                    )) ||
                    Boolean(e.currentTreatments.find(
                        (x) => x.state === ProcessState.EXAM_IN_PROGRESS
                    ))
                );
            })
        );
    }

    public hasNarrativeFlag(_id: TFlagId): boolean {
        return this._saveSlotData[this._activeSaveSlot]
            .narrativeState.flags
            .indexOf(_id) !== -1;
    }

    public addNarrativeFlag(_id: TFlagId): void {
        const flags = this._saveSlotData[this._activeSaveSlot].narrativeState.flags;
        if(flags.indexOf(_id) !== -1) {
            console.warn("Flag %s already in saved data", _id);
            return;
        }
        flags.push(_id);
        this.dirtify(SAVE_KEYS.NarrativeState);
    }

    private getDayTimer(): number {
        return this._saveSlotData[this._activeSaveSlot].currentDayTimer;
    }

    public addToDayTimer(_amt: number): void {
        const addition = Math.min(
            _amt + this.getDayTimer(),
            (1000 * 60 * 60 * 24) - (DAY_START_TIMESTAMP)
        );
        if(GAME_DEBUG_MODE) {
            console.log("Adding %i to day timer", addition - this.getDayTimer());
        }
        this.setDayTimer(addition);
    }

    public setDayTimer(_amt: number): void {
        this._saveSlotData[this._activeSaveSlot].currentDayTimer =
            Math.min(
                _amt,
                (1000 * 60 * 60 * 24) - (DAY_START_TIMESTAMP)
            );

        // HACK
        if(!_UPDATE_GAME_TIME_TIMER) {
            _UPDATE_GAME_TIME_TIMER = setTimeout(
                () => {
                    this.dirtify(SAVE_KEYS.CurrentDayTimer);
                    _UPDATE_GAME_TIME_TIMER = null;
                },
                2500) as unknown as number;
        }
    }

    public getDay(): number {
        return this._saveSlotData[this._activeSaveSlot].day;
    }

    public setDay(_day: number): void {
        this._saveSlotData[this._activeSaveSlot].day = _day;
        this.dirtify(SAVE_KEYS.Day);
    }

    public getContacts(): TContactID[] {
        return this._saveSlotData[this._activeSaveSlot].contacts;
    }

    public setContacts(_contacts: TContactID[]): void {
        this._saveSlotData[this._activeSaveSlot].contacts
            = _contacts;
        this.dirtify(SAVE_KEYS.Contacts);
    }

    public setGameMode(_val: GameModes): void {
        this.getSaveSlotDataRef().gameMode = _val;
        this.dirtify(SAVE_KEYS.GameMode);
    }

    public getGameMode(): GameModes {
        return this.getSaveSlotDataRef().gameMode;
    }

    public setCase(_case: IMedCaseJSON): void {
        const ind = this.getSaveSlotDataRef().activeCases.findIndex((e) => e.id === _case.id);
        if(ind === -1) {
            throw new Error("Failed to find case " + _case.id);
        }
        this.getSaveSlotDataRef().activeCases[ind] = _case;
        this.dirtify(SAVE_KEYS.ActiveCases);
    }

    public getGameVersion(_versionStr?: string): {
        major: number,
        minor: number,
        patch: number,
    } {
        const retVal = {
            major: 0,
            minor: 0,
            patch: 0
        };
        const split = (_versionStr || this.getSaveSlotDataRef().gameVersion).split(".");
        if(split.length !== 3) {
            return retVal;
        }

        retVal.major = parseInt(split[0]);
        retVal.minor = parseInt(split[1]);
        retVal.patch = parseInt(split[2]);

        return retVal;
    }

    public getGameVersionStr(): string {
        return this.getSaveSlotDataRef().gameVersion;
    }

    public setGameVersionStr(_val: string): void {
        this.getSaveSlotDataRef().gameVersion = _val;
        this.dirtify(SAVE_KEYS.Version);
    }

    public addDocScore(_val: number): void {
        if(typeof _val === "undefined" || Number.isNaN(_val)) {
            _val = 0;
        }
        this.getSaveSlotDataRef().docScore += Math.max(0, _val);
        this.dirtify(SAVE_KEYS.DocScore);
    }

    private dbg_setDocScore(_val: number): void {
        this.getSaveSlotDataRef().docScore = _val;
        this.dirtify(SAVE_KEYS.DocScore);
    }

    public getDocScore(): number {
        return this.getSaveSlotDataRef().docScore;
    }

    public incrementNumOfSolvedCases(): void {
        this.getSaveSlotDataRef().numOfSolvedCases += 1;
        this.dirtify(SAVE_KEYS.NumOfSolvedCases);
    }

    public incrementNumOfIncorrectDiagnoses(): void {
        this.getSaveSlotDataRef().numOfIncorrectDiagnoses += 1;
        this.dirtify(SAVE_KEYS.NumOfIncorrectDiagnoses);
    }

    public getNumOfSolvedCases(): number {
        return this.getSaveSlotDataRef().numOfSolvedCases;
    }

    public getUse24HrClock(): boolean {
        return true; // this._use24hrClock;
    }

    public setUse24HrClock(_val: boolean) {
        this._use24hrClock = _val;
        this.dirtify(SAVE_KEYS.Use24HrClock);
    }

    public addAchievement(_id: AchievementID): void {
        if(this.getSaveSlotDataRef().achievements.indexOf(_id) === -1) {
            this.getSaveSlotDataRef().achievements.push(_id);
            ENGINE.logEvent(
                AnalyticsEventID.AchievementGet,
                _id,
            );
            this.dirtify(SAVE_KEYS.Achievements);
        }
    }

    public getAchievements(): AchievementID[] {
        return this.getSaveSlotDataRef().achievements;
    }

    public hasAchievement(_id: AchievementID): boolean {
        return this.getSaveSlotDataRef().achievements.indexOf(_id) !== -1;
    }

    public getNumOfIncorrectDiagnoses(): number {
        return this.getSaveSlotDataRef().numOfIncorrectDiagnoses;
    }

    public getDisplayName(): string {
        return this.getSaveSlotDataRef().displayName;
    }

    public getCharacterCreatorSettings(): ICharacterGeneratorSettings {
        return this.getSaveSlotDataRef().characterCreatorSettings;
    }

    public getPastCases(): Record<string, IMedCaseJSON> {
        return this.getSaveSlotDataRef().pastCases;
    }

    public addPastCase(_caseJSON: IMedCaseJSON): void {
        if(this.getSaveSlotDataRef().pastCases[_caseJSON.id]) {
            console.warn("Attempted to add dupe past case");
            return;
        }

        this.getSaveSlotDataRef().pastCases[_caseJSON.id] =
            Object.assign({}, _caseJSON);

        this.dirtify(SAVE_KEYS.PastCases);
    }

    public getIsMusicMuted(): boolean {
        return this._muteMusic;
    }

    public setMusicMuted(_val: boolean): void {
        this._muteMusic = _val;
        this.dirtify(SAVE_KEYS.MusicMuted);
    }

    public getIsSFXMuted(): boolean {
        return this._muteSFX;
    }

    public setIsSFXMuted(_val: boolean): void {
        this._muteSFX = _val;
        this.dirtify(SAVE_KEYS.MuteSFX);
    }

    public getKnownUrls(): string[] {
        return [...this._knownUrls];
    }

    public getShouldAutoFullscreen(): boolean {
        return this._shouldAutoFullscreen;
    }

    public setShouldAutoFullscreen(val: boolean): void {
        this._shouldAutoFullscreen = val;
        this.dirtify(SAVE_KEYS.ShouldAutoFullscreen);
    }

    public addKnownUrl(_url: string): void {
        if(this._knownUrls.indexOf(_url) === -1) {
            this._knownUrls.push(_url);
            this.dirtify(SAVE_KEYS.KnownUrls);
        }
    }

    public getCRTEffectEnabled(): boolean {
        return this._crtEffectEnabled;
    }

    public setCRTEffectEnabled(_val: boolean): void {
        this._crtEffectEnabled = _val;
        this.dirtify(SAVE_KEYS.CRTEffectEnabled);
    }

    public getCopyOfMailboxContents(): IMailInstanceData[] {
        return [...this.getSaveSlotDataRef().mailbox];
    }

    public getMailboxZoom(): number {
        return this._mailboxZoom;
    }

    public setMailboxZoom(_val: number): void {
        if(typeof _val === "string") {
            this._mailboxZoom = parseInt(_val);
        } else {
            this._mailboxZoom = _val;
        }
        this.dirtify(SAVE_KEYS.MailboxZoomFactor);
    }

    /**
     * Don't use unless you know what you're doing!
     */
    public getMailboxContents(): IMailInstanceData[] {
        return this.getSaveSlotDataRef().mailbox;
    }

    public setMailboxContents(contents: IMailInstanceData[]): void {
        this.getSaveSlotDataRef().mailbox = contents;
    }

    public setSeenFTUTutorial(_boolean: boolean): void {
        this.getSaveSlotDataRef().seenFTUTutorial = true;
        this.dirtify(SAVE_KEYS.SeenFTUTutorial);
    }

    public hasSeenFTUTutorial(): boolean {
        return this.getSaveSlotDataRef().seenFTUTutorial;
    }

    initialize(_data: Record<string, unknown>): void {
        const data = _data || {} as Record<string, unknown>;
        if (this.isInitialized()) {
            console.warn("PlayerDataSingleton being initialized multiple times");
        }
        this._authKey = data[SAVE_KEYS.AuthKey] as string || "";
        this._authPlatform = data[SAVE_KEYS.AuthPlatform] as string || "none";

        this._muteMusic =
            data[SAVE_KEYS.MusicMuted] ?
                (data[SAVE_KEYS.MusicMuted] as string) === "true"
                :
                this._muteMusic;

        this._muteSFX =
            data[SAVE_KEYS.MuteSFX] ?
                (data[SAVE_KEYS.MuteSFX] as string) === "true"
                :
                this._muteSFX;

        this._crtEffectEnabled =
            data[SAVE_KEYS.CRTEffectEnabled] ?
                (data[SAVE_KEYS.CRTEffectEnabled] as string) === "true"
                :
                this._crtEffectEnabled;

        this._shouldAutoFullscreen =
            data[SAVE_KEYS.ShouldAutoFullscreen] ?
                (data[SAVE_KEYS.ShouldAutoFullscreen] as string) === "true"
                :
                this._shouldAutoFullscreen;

        this._mailboxZoom =
            data[SAVE_KEYS.MailboxZoomFactor] ?
                parseInt(data[SAVE_KEYS.MailboxZoomFactor] as string)
                :
                this._mailboxZoom;

        for(let _i = 0; _i < 3; _i++) {
            const i: 0 | 1 | 2 = _i as 0 | 1 | 2;
            let key: SAVE_KEYS;
            switch(i) {
                case 0:
                    key = SAVE_KEYS.SaveSlot0;
                    break;
                case 1:
                    key = SAVE_KEYS.SaveSlot1;
                    break;
                case 2:
                    key = SAVE_KEYS.SaveSlot2;
                    break;
            }
            try {
                if(typeof data[key] == "string") {
                    this._saveSlotData[i] =
                        data[key] ?
                            JSON.parse(data[key] as string)
                            :
                            this._saveSlotData[i];
                } else {
                    this._saveSlotData[i] =
                        data[key] ?
                            data[key] as ISlotData
                            :
                            this._saveSlotData[i];
                }
            } catch(err) {
                console.error("Failed to parse save slot %i!", i);
                console.error(err);
            }
        }

        // this.getSaveSlotDataRef().seenFTUTutorial =
        //     data[SAVE_KEYS.SeenFTUTutorial] ?
        //         (data[SAVE_KEYS.SeenFTUTutorial] as string) === "true"
        //         :
        //         this.getSaveSlotDataRef().seenFTUTutorial;
        //
        // this.getSaveSlotDataRef().gameVersion =
        //     data[SAVE_KEYS.Version] ?
        //         (data[SAVE_KEYS.Version] as string)
        //         :
        //         this.getSaveSlotDataRef().gameVersion;
        //
        // this.getSaveSlotDataRef().numOfIncorrectDiagnoses =
        //     data[SAVE_KEYS.NumOfIncorrectDiagnoses] ?
        //         (data[SAVE_KEYS.NumOfIncorrectDiagnoses] as number)
        //         :
        //         this.getSaveSlotDataRef().numOfIncorrectDiagnoses;
        //
        // this.getSaveSlotDataRef().numOfSolvedCases =
        //     data[SAVE_KEYS.NumOfSolvedCases] ?
        //         (data[SAVE_KEYS.NumOfSolvedCases] as number)
        //         :
        //         this.getSaveSlotDataRef().numOfSolvedCases;
        //
        // this.getSaveSlotDataRef().docScore =
        //     data[SAVE_KEYS.DocScore] ?
        //         (data[SAVE_KEYS.DocScore] as number)
        //         :
        //         this.getSaveSlotDataRef().docScore;
        //
        // this.getSaveSlotDataRef().gameMode =
        //     data[SAVE_KEYS.GameMode] ?
        //         (data[SAVE_KEYS.GameMode] as GameModes)
        //         :
        //         this.getSaveSlotDataRef().gameMode;
        // try {
        //     if(typeof data[SAVE_KEYS.CharacterCreatorSettings] == "string") {
        //         this.getSaveSlotDataRef().characterCreatorSettings =
        //             data[SAVE_KEYS.CharacterCreatorSettings] ?
        //                 JSON.parse(data[SAVE_KEYS.CharacterCreatorSettings] as string)
        //                 :
        //                 this.getSaveSlotDataRef().characterCreatorSettings;
        //     } else {
        //         this.getSaveSlotDataRef().characterCreatorSettings =
        //             data[SAVE_KEYS.CharacterCreatorSettings] ?
        //                 data[SAVE_KEYS.CharacterCreatorSettings] as ICharacterGeneratorSettings
        //                 :
        //                 this.getSaveSlotDataRef().characterCreatorSettings;
        //     }
        // } catch(err) {
        //     console.error("Failed to parse character settings!");
        //     console.error(err);
        // }
        //
        // try {
        //     if(typeof data[SAVE_KEYS.Birthdate] == "string") {
        //         this.getSaveSlotDataRef().birthdate =
        //             data[SAVE_KEYS.Birthdate] ?
        //                 JSON.parse(data[SAVE_KEYS.Birthdate] as string)
        //                 :
        //                 this.getSaveSlotDataRef().birthdate;
        //     } else {
        //         this.getSaveSlotDataRef().birthdate =
        //             data[SAVE_KEYS.Birthdate] ?
        //                 data[SAVE_KEYS.Birthdate] as {
        //                     day: number;
        //                     month: number;
        //                 }
        //                 :
        //                 this.getSaveSlotDataRef().birthdate;
        //     }
        // } catch(err) {
        //     console.error("Failed to parse birthday!");
        //     console.error(err);
        // }
        //
        // try {
        //     if(typeof data[SAVE_KEYS.ActiveCases] == "string") {
        //         this.getSaveSlotDataRef().activeCases =
        //             data[SAVE_KEYS.ActiveCases] ?
        //                 JSON.parse(data[SAVE_KEYS.ActiveCases] as string)
        //                 :
        //                 this.getSaveSlotDataRef().activeCases;
        //     } else {
        //         this.getSaveSlotDataRef().activeCases =
        //             data[SAVE_KEYS.ActiveCases] ?
        //                 data[SAVE_KEYS.ActiveCases] as Array<IMedCaseJSON>
        //                 :
        //                 this.getSaveSlotDataRef().activeCases;
        //     }
        // } catch(err) {
        //     console.error("Failed to parse current cases!");
        //     console.error(err);
        // }
        //
        // try {
        //     if(typeof data[SAVE_KEYS.PastCases] == "string") {
        //         this.getSaveSlotDataRef().pastCases =
        //             data[SAVE_KEYS.PastCases] ?
        //                 JSON.parse(data[SAVE_KEYS.PastCases] as string)
        //                 :
        //                 this.getSaveSlotDataRef().pastCases;
        //     } else {
        //         this.getSaveSlotDataRef().pastCases =
        //             data[SAVE_KEYS.PastCases] ?
        //                 data[SAVE_KEYS.PastCases] as Record<string, IMedCaseJSON>
        //                 :
        //                 this.getSaveSlotDataRef().pastCases;
        //     }
        // } catch(err) {
        //     console.error("Failed to parse past cases!");
        //     console.error(err);
        // }
        //
        // try {
        //     if(typeof data[SAVE_KEYS.MailboxContents] == "string") {
        //         this.getSaveSlotDataRef().mailbox =
        //             data[SAVE_KEYS.MailboxContents] ?
        //                 JSON.parse(data[SAVE_KEYS.MailboxContents] as string)
        //                 :
        //                 this.getSaveSlotDataRef().mailbox;
        //     } else {
        //         this.getSaveSlotDataRef().mailbox =
        //             data[SAVE_KEYS.MailboxContents] ?
        //                 data[SAVE_KEYS.MailboxContents] as IMailInstanceData[]
        //                 :
        //                 this.getSaveSlotDataRef().mailbox;
        //     }
        // } catch(err) {
        //     console.error("Failed to parse mailbox contents!");
        //     console.error(err);
        //
        //     try {
        //         // make a backup... just in case.
        //         window.localStorage.setItem(
        //             "hos_BACKUP_MAILDATA",
        //             data[SAVE_KEYS.MailboxContents] as string
        //         );
        //     } catch(err) {
        //         console.error("Failed to backup broken mailbox contents!");
        //         console.error(err);
        //     }
        // }
        try {
            if(typeof data[SAVE_KEYS.KnownUrls] == "string") {
                this._knownUrls =
                    data[SAVE_KEYS.KnownUrls] ?
                        JSON.parse(data[SAVE_KEYS.KnownUrls] as string)
                        :
                        this._knownUrls;
            } else {
                this._knownUrls =
                    data[SAVE_KEYS.KnownUrls] ?
                        data[SAVE_KEYS.KnownUrls] as string[]
                        :
                        this._knownUrls;
            }
        } catch(err) {
            console.error("Failed to parse known urls!");
            console.error(err);
        }

        this._initialized = true;
    }

    public initializeFromSlotData(_slotData?: ISlotData): void {
        const slot = this._activeSaveSlot as -1 | 0 | 1 | 2;
        if(!_slotData && slot === -1)
            throw new Error("Invalid save slot");
        else if(!_slotData && slot !== -1)
            _slotData = this._saveSlotData[this._activeSaveSlot];

        // this._achievements = _slotData.achievements;
        // this._mailbox = _slotData.mailbox;
        // this._gameMode = _slotData.gameMode;
        // this._gameVersion = _slotData.gameVersion;
        // this._numOfSolvedCases = _slotData.numOfSolvedCases;
        // this._numOfIncorrectDiagnoses = _slotData.numOfIncorrectDiagnoses;
        // this._pastCases = _slotData.pastCases;
        // this._docScore = _slotData.docScore;
        // this._seenFTUTutorial = _slotData.seenFTUTutorial;
        // this._characterCreatorSettings = _slotData.characterCreatorSettings;
        // this._activeCases = _slotData.activeCases;
        // this._displayName = _slotData.displayName;
        // this._birthdate = _slotData.birthdate;
        // this._hometown = _slotData.hometown;
        // timestamp
    }

    public getAuthKey(): string {
        return this._authKey || "";
        // return "";
    }

    public getAuthPlatform(): string {
        return this._authPlatform || "offline";
        // return "";
    }

    public setAuthKey(authKey: string, platform: "itch" | "google" | "offline"): void {
        this._authKey = authKey;
        this._authPlatform = platform;
        this.dirtify(SAVE_KEYS.AuthKey);
    }

    public clearCases(): void {
        this.getSaveSlotDataRef().activeCases = [];
        this.dirtify(SAVE_KEYS.ActiveCases);
    }

    public getCases(): IMedCaseJSON[] {
        return this.getSaveSlotDataRef().activeCases;
    }

    public addCase(_case: IMedCaseJSON): void {
        this.getSaveSlotDataRef().activeCases.push(_case);
        this.dirtify(SAVE_KEYS.ActiveCases);
    }

    public removeCase(caseOrID: string | IMedCaseJSON): void {
        if(typeof caseOrID === "string") {
            const index = this.getSaveSlotDataRef().activeCases.findIndex((e) => e.id === caseOrID);
            if(index === -1) {
                return;
            }
            this.getSaveSlotDataRef().activeCases.splice(index, 1);
        } else {
            const index = this.getSaveSlotDataRef().activeCases.indexOf(caseOrID);
            if(index === -1) {
                return;
            }
            this.getSaveSlotDataRef().activeCases.splice(index, 1);
        }

        this.dirtify(SAVE_KEYS.ActiveCases);
    }

    public getUserData(): IUserData {
        return JSON.parse(JSON.stringify({
            achievements: this.getSaveSlotDataRef().achievements,
            displayName: this.getSaveSlotDataRef().displayName,
            docScore: this.getSaveSlotDataRef().docScore,
            id: "",
            numOfIncorrectDiagnoses: this.getSaveSlotDataRef().numOfIncorrectDiagnoses,
            numOfSolvedCases: this.getSaveSlotDataRef().numOfSolvedCases
        }));
    }

    public export(_exportAll: boolean = false): { [key: string]: unknown } {
        const retVal: { [key: string]: unknown } = {};

        const shouldExportSaveSlot = Boolean(
            this._dirty.find((e) => PER_SLOT_KEYS.indexOf(e) !== -1)
        );

        // shared settings
        if(
            (_exportAll || this._dirty.indexOf(SAVE_KEYS.AuthKey) !== -1)
        ) {
            retVal[SAVE_KEYS.AuthKey] = this._authKey;
            retVal[SAVE_KEYS.AuthPlatform] = this._authPlatform;
            // document.cookie = `${SAVE_KEYS.AuthKey}=${this.getAuthKey()}; expires=${
            //     formatDateForCookie(Date.now() + (1000 * 60 * 60 * 24 * 30))
            // }; path=/`;
        }
        if(
            (_exportAll || this._dirty.indexOf(SAVE_KEYS.CRTEffectEnabled) !== -1)
        ) {
            retVal[SAVE_KEYS.CRTEffectEnabled] = this._crtEffectEnabled;
        }
        if(
            (_exportAll || this._dirty.indexOf(SAVE_KEYS.MusicMuted) !== -1)
        ) {
            retVal[SAVE_KEYS.MusicMuted] = this._muteMusic;
        }
        if(
            (_exportAll || this._dirty.indexOf(SAVE_KEYS.MuteSFX) !== -1)
        ) {
            retVal[SAVE_KEYS.MuteSFX] = this._muteSFX;
        }
        if(
            (_exportAll || this._dirty.indexOf(SAVE_KEYS.KnownUrls) !== -1)
        ) {
            retVal[SAVE_KEYS.KnownUrls] = JSON.stringify(
                this._knownUrls
            );
        }
        if(
            (_exportAll || this._dirty.indexOf(SAVE_KEYS.ShouldAutoFullscreen) !== -1)
        ) {
            retVal[SAVE_KEYS.ShouldAutoFullscreen] = this._shouldAutoFullscreen;
        }
        if(
            (_exportAll || this._dirty.indexOf(SAVE_KEYS.MailboxZoomFactor) !== -1)
        ) {
            retVal[SAVE_KEYS.MailboxZoomFactor] = this._mailboxZoom;
        }

        // per slot data
        const slot = this._activeSaveSlot as -1 | 0 | 1 | 2;
        if(shouldExportSaveSlot && slot !== -1) {
            const slotData: ISlotData =
                Object.assign({}, this.getSaveSlotDataRef());

            slotData.mailbox =
                slotData.mailbox.filter((e) => !MailCore.isEmailACase(e.id, e.type));

            if(this._dirty.length > 0) {
                slotData.timestamp = Date.now();
            }

            switch(this._activeSaveSlot) {
                case 0:
                    retVal[SAVE_KEYS.SaveSlot0] = JSON.stringify(slotData);
                    break;
                case 1:
                    retVal[SAVE_KEYS.SaveSlot1] = JSON.stringify(slotData);
                    break;
                case 2:
                    retVal[SAVE_KEYS.SaveSlot2] = JSON.stringify(slotData);
                    break;
                default:
                    throw new Error("Invalid active slot");
            }
        }

        this._dirty = [];

        return retVal;
    }

    public resetAllData(): void {
        // HACK
        try {
            window.localStorage.clear();
        } catch(err) {
            console.error(err);
        }
        // HACK

        PlayerDataSingleton = new PlayerDataSingletonClass();
        // PlayerDataSingleton._gameVersion = __VERSION;
        // PlayerDataSingleton.dirtify(SAVE_KEYS.Version);
    }
}

export let PlayerDataSingleton = new PlayerDataSingletonClass();

if(GAME_DEBUG_MODE || ENGINE_DEBUG_MODE) {
    // @ts-ignore
    window["PlayerDataSingleton"] = PlayerDataSingleton;
}
